import React, { useState, useEffect, useMemo } from "react";
import {
  Input,
  Row,
  Card,
  Table,
  Spin,
  Button,
  Space,
  Form,
  Pagination,
} from "antd";
import { ArrowDownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import ArrowLeft from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import { BOM } from "../../../services/definedMethod";

const DUMMY_AUCTIONS = [
  {
    id: 1,
    auctionDate: "2026-02-22",
    settlementDate: "2026-02-24",
    maturityDate: "2026-05-25",
    totalOffered: 1_000_000_000.0,
    status: "OPEN",
    instrumentType: "TREASURY_BILL",
    currencyCode: "NGN",
  },
  {
    id: 2,
    auctionDate: "2026-03-01",
    settlementDate: "2026-03-03",
    maturityDate: "2026-06-01",
    totalOffered: 750_000_000.0,
    status: "OPEN",
    instrumentType: "TREASURY_BILL",
    currencyCode: "NGN",
  },
  {
    id: 3,
    auctionDate: "2026-03-08",
    settlementDate: "2026-03-10",
    maturityDate: "2026-09-07",
    totalOffered: 500_000_000.0,
    status: "CLOSED",
    instrumentType: "TREASURY_BOND",
    currencyCode: "NGN",
  },
  {
    id: 4,
    auctionDate: "2026-03-15",
    settlementDate: "2026-03-17",
    maturityDate: "2026-06-15",
    totalOffered: 2_000_000_000.0,
    status: "PUBLISHED",
    instrumentType: "TREASURY_BILL",
    currencyCode: "USD",
  },
  {
    id: 5,
    auctionDate: "2026-03-22",
    settlementDate: "2026-03-24",
    maturityDate: "2026-12-21",
    totalOffered: 1_500_000_000.0,
    status: "PENDING",
    instrumentType: "TREASURY_BOND",
    currencyCode: "NGN",
  },
];

// Status badge colours
const STATUS_COLORS = {
  OPEN:      { bg: "#ECFDF3", color: "#027A48" },
  CLOSED:    { bg: "#FEF3F2", color: "#B42318" },
  PUBLISHED: { bg: "#EFF8FF", color: "#175CD3" },
  PENDING:   { bg: "#FFFAEB", color: "#B54708" },
};

const StatusBadge = ({ status }) => {
  const style = STATUS_COLORS[status] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 99,
        fontSize: 12,
        fontWeight: 600,
        background: style.bg,
        color: style.color,
      }}
    >
      {status}
    </span>
  );
};

// ── Component ──────────────────────────────────────────────────────────────
const ManageAuction = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData || {};

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [originalList, setOriginalList] = useState([]);
  const [loading, setLoading] = useState(false);

  const pageSize = 10;

  const columns = [
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          Auction Date{" "}
          <ArrowDownOutlined style={{ fontSize: 14, color: "gray" }} />
        </div>
      ),
      dataIndex: "auctionDate",
      sorter: (a, b) => a.auctionDate.localeCompare(b.auctionDate),
    },
    {
      title: "Settlement Date",
      dataIndex: "settlementDate",
    },
    {
      title: "Maturity Date",
      dataIndex: "maturityDate",
    },
    {
      title: "Instrument Type",
      dataIndex: "instrumentType",
    },
    {
      title: "Currency",
      dataIndex: "currencyCode",
    },
    {
      title: "Total Offered",
      dataIndex: "totalOffered",
      render: (val) =>
        val != null
          ? val.toLocaleString("en-NG", { minimumFractionDigits: 2 })
          : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val) => <StatusBadge status={val} />,
    },
  ];

  useEffect(() => {
    getAuctionList();
  }, []);

  useEffect(() => {
    if (BOM.HasPermission?.("CREATE_FINANCIAL_INSTRUMENT")) {
      rightAction(cnt.RIGHT_ADD_BTN);
    }
  }, []);

  const getAuctionList = () => {
    setLoading(true);

    // ── Swap the block below for your real API call when ready ──
    // const body = { session };
    // const url = `${apiConfig.GetAuctions}?status=OPEN`;
    // BOM.FetchReqAction(body, url, (err, res) => { ... });

    setTimeout(() => {
      const mapped = DUMMY_AUCTIONS.map((el) => ({
        key: el.id,
        id: el.id,
        auctionDate: el.auctionDate,
        settlementDate: el.settlementDate,
        maturityDate: el.maturityDate,
        instrumentType: el.instrumentType,
        currencyCode: el.currencyCode,
        totalOffered: el.totalOffered,
        status: el.status,
      }));

      setOriginalList(mapped);
      setSelectedRow([]);
      setCurrentPage(1);
      setLoading(false);
    }, 400);
  };

  const handleSearchInTable = (e) => {
    setSearchTerm(e.target.value || "");
    setCurrentPage(1);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return originalList;
    const q = searchTerm.toLowerCase();
    return originalList.filter(
      (item) =>
        item.auctionDate?.toLowerCase().includes(q) ||
        item.instrumentType?.toLowerCase().includes(q) ||
        item.currencyCode?.toLowerCase().includes(q) ||
        item.status?.toLowerCase().includes(q)
    );
  }, [originalList, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const itemRender = (_, type, el) => {
    if (type === "prev" || type === "next") return;
    return el;
  };

  const onSelectItemAction = (selectedRowKeys, selectedRows) => {
    if (!selectedRowKeys || selectedRowKeys.length !== 1) {
      setSelectedRow([]);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });
      if (BOM.HasPermission?.("CREATE_FINANCIAL_INSTRUMENT")) {
        dispatch({ type: cnt.RIGHT_ADD_BTN });
      }
      return;
    }

    setSelectedRow(selectedRows);

    if (BOM.HasPermission?.("READ_SECURITY_PRODUCT")) {
      dispatch({ type: cnt.RIGHT_DETAIL_BTN });
    }
    if (BOM.HasPermission?.("UPDATE_SECURITY_PRODUCT")) {
      dispatch({ type: cnt.RIGHT_EDIT_BTN });
    }
  };

  const handleOnView = () => {
    if (rightBtn?.detailBtn === "primary") {
      navigate("/view-auction", { state: selectedRow[0]?.id });
    }
  };

  const handleOnCreate = () => {
    if (rightBtn?.addBtn === "primary") {
      navigate("/add-auction");
    }
  };

  const handleOnEdit = () => {
    if (rightBtn?.editBtn === "primary") {
      navigate("/edit-auction", { state: selectedRow[0]?.id });
    }
  };

  return (
    <MainLayout
      isEnableRightSidebar
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
      pageItems={staticConfig.manageAuction}
    >
      <TopContent
        pageTitle={labelConfig.auction}
        createPageTitle={labelConfig.auctionListing}
      />

      <Card
        style={{ border: "1px solid #EAECF0", borderRadius: 20, marginTop: 12 }}
      >
        <Spin spinning={loading}>
          <Row
            style={{
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Form form={form}>
              <Space size={12}>
                <Input
                  value={searchTerm}
                  onChange={handleSearchInTable}
                  placeholder="Search auctions…"
                  prefix={<SearchLg width={18} color="#667085" />}
                  style={{ width: 350, borderRadius: 10 }}
                />
              </Space>
            </Form>
          </Row>

          <Table
            rowSelection={{
              type: "checkbox",
              hideSelectAll: true,
              onChange: onSelectItemAction,
            }}
            columns={columns}
            dataSource={paginatedData}
            pagination={false}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            <Button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            >
              <ArrowLeft /> Previous
            </Button>

            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              showSizeChanger={false}
              itemRender={itemRender}
              onChange={setCurrentPage}
            />

            <Button
              disabled={currentPage >= totalPages}
              onClick={() =>
                setCurrentPage((p) => Math.min(p + 1, totalPages))
              }
            >
              Next <ArrowRight />
            </Button>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
});

export default connect(mapStateToProps, { rightAction })(ManageAuction);